import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { accountActions } from "../slices/account";
import { getErrorMessage } from "../../utils/errorUtils";
import { AccountRequestDto } from "../../shared/dtos/account/AccountRequestDto";
import { AccountResponseDto } from "../../shared/dtos/account/AccountResponseDto";
import { UpdateAccountPayload } from "../../shared/interfaces/account/UpdateAccountPayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getAccounts =
  (params?: PaginationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(accountActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<AccountResponseDto>>
      >("/accounts", { headers: { requiresAuth: true }, params });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(accountActions.setItems(data.data.content));
        dispatch(accountActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(accountActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getAccountById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(accountActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<AccountResponseDto>
      >(`/accounts/${id}`, { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(accountActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(accountActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createAccount =
  (values: AccountRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(accountActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<AccountResponseDto>
      >("/accounts", values, { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Account created successfully!");
        dispatch(accountActions.createSuccess(data.data));
        dispatch(
          accountActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(accountActions.setErrorCreate(message));
    }
  };

export const updateAccount =
  (payload: UpdateAccountPayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(accountActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<AccountResponseDto>
      >(`/accounts/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Account updated successfully!");
        dispatch(accountActions.updateSuccess(data.data));
        dispatch(
          accountActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(accountActions.setErrorUpdate(message));
    }
  };

export const deleteAccount =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(accountActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/accounts/${id}`,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Account deleted successfully!");
        dispatch(accountActions.deleteSuccess(id));
        dispatch(
          accountActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(accountActions.setErrorDelete(message));
    }
  };

export const resetAccountServerStatus = () => (dispatch: AppDispatch) =>
  dispatch(accountActions.setServerStatus(null));
